import Styles from './CalendarEventCard.module.scss'
import VariantStyles from './LoadingColorState.module.scss'
import cx from 'classnames'
import { ComponentType, CSSProperties } from 'react'
import { AgenticStepIndicator } from 'components/AgenticStepIndicator/AgenticStepIndicator'
import { useImageLuminance } from 'hooks/useImageLuminance'

export type LoadingColorStateVariant = 'default' | 'blog' | 'newsletter'

export interface LoadingColorStateProps {
  loadingColor: string
  progress?: number
  stepLabel?: string | null
  stepIcon?: ComponentType<{ size?: number; color?: string }>
  referenceImageUrl?: string | null
  generationStartedAt?: string | null
  variant?: LoadingColorStateVariant
}

const LOREM_TITLE = 'Lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt'

const LOREM_BODY =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.'

const LOREM_DATE = 'Lorem 1, 2026'

export const LoadingColorState = ({
  loadingColor,
  stepLabel,
  stepIcon,
  referenceImageUrl,
  generationStartedAt,
  variant = 'default',
}: LoadingColorStateProps) => {
  const hasReferenceImage = !!referenceImageUrl
  // Image is at 90% opacity over near-white bg (~245 luminance for --dark-4)
  const isDark = useImageLuminance(referenceImageUrl, { bgLuminance: 245, opacity: 0.9, regions: ['ML', 'MC', 'MR'] })
  const textColor = hasReferenceImage && isDark !== false ? 'var(--light-60)' : 'var(--dark-40)'

  if (variant === 'blog' || variant === 'newsletter') {
    return (
      <ArticleVariant
        variant={variant}
        referenceImageUrl={referenceImageUrl}
        stepLabel={stepLabel}
        stepIcon={stepIcon}
        generationStartedAt={generationStartedAt}
        textColor={textColor}
      />
    )
  }

  const styles = {
    '--brand-kit-color': loadingColor,
  } as CSSProperties

  return (
    <div
      className={cx(Styles.loadingColorState, Styles.hasStepData, {
        [Styles.hasReferenceImage]: hasReferenceImage,
      })}
      style={styles}
      data-testid="loading-color-state"
    >
      {hasReferenceImage && <img src={referenceImageUrl} className={Styles.referenceImage} alt="" />}
      <AgenticStepIndicator
        label={stepLabel || 'Generating'}
        color={textColor}
        icon={stepIcon}
        startedAt={generationStartedAt}
      />
    </div>
  )
}

interface ArticleVariantProps {
  variant: 'blog' | 'newsletter'
  referenceImageUrl?: string | null
  stepLabel?: string | null
  stepIcon?: ComponentType<{ size?: number; color?: string }>
  generationStartedAt?: string | null
  textColor: string
}

const ArticleVariant = ({
  variant,
  referenceImageUrl,
  stepLabel,
  stepIcon,
  generationStartedAt,
  textColor,
}: ArticleVariantProps) => {
  const isNewsletter = variant === 'newsletter'
  return (
    <div className={VariantStyles.variantContainer} data-testid={`loading-color-state-${variant}`}>
      <div className={VariantStyles.blurred} aria-hidden>
        {isNewsletter ? (
          <NewsletterLayout referenceImageUrl={referenceImageUrl} />
        ) : (
          <BlogLayout referenceImageUrl={referenceImageUrl} />
        )}
      </div>
      <div className={VariantStyles.variantIndicator}>
        <AgenticStepIndicator
          label={stepLabel || 'Generating'}
          color={textColor}
          icon={stepIcon}
          startedAt={generationStartedAt}
        />
      </div>
    </div>
  )
}

const ImageSlot = ({ referenceImageUrl }: { referenceImageUrl?: string | null }) => (
  <div className={VariantStyles.variantImageSlot} data-testid="loading-color-state-image-slot">
    {referenceImageUrl ? (
      <img src={referenceImageUrl} alt="" className={VariantStyles.variantImage} />
    ) : (
      <div className={VariantStyles.variantImagePlaceholder} data-testid="loading-color-state-image-placeholder" />
    )}
  </div>
)

const BlogLayout = ({ referenceImageUrl }: { referenceImageUrl?: string | null }) => (
  <div className={VariantStyles.blogLayout} data-testid="loading-color-state-blog-layout">
    <ImageSlot referenceImageUrl={referenceImageUrl} />
    <div className={VariantStyles.variantTitle}>{LOREM_TITLE}</div>
    <div className={VariantStyles.variantDate}>{LOREM_DATE}</div>
    <div className={VariantStyles.variantBody}>{LOREM_BODY}</div>
  </div>
)

const NewsletterLayout = ({ referenceImageUrl }: { referenceImageUrl?: string | null }) => (
  <div className={VariantStyles.newsletterLayout} data-testid="loading-color-state-newsletter-layout">
    <div className={VariantStyles.variantTitle}>{LOREM_TITLE}</div>
    <ImageSlot referenceImageUrl={referenceImageUrl} />
    <div className={VariantStyles.variantBody}>{LOREM_BODY}</div>
  </div>
)
